import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../api';
import { setSentryUser } from '../sentry';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setSentryUser(parsedUser);
        authAPI.getMe()
          .then(response => {
            setUser(response.data);
            setSentryUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
          })
          .catch(() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setSentryUser(null);
          })
          .finally(() => setLoading(false));
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setSentryUser(null);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    
    const userResponse = await authAPI.getMe();
    setUser(userResponse.data);
    setSentryUser(userResponse.data);
    localStorage.setItem('user', JSON.stringify(userResponse.data));
    return userResponse.data;
  };

  const signup = async (email, password, fullName) => {
    const response = await authAPI.signup(email, password, fullName);
    return response.data;
  };

  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      setSentryUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch {
      return null;
    }
  }, []);

  const logout = async () => {
    await authAPI.logout();
    setUser(null);
    setSentryUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, refreshUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
